import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import { 
  ArrowLeft, Building2, MapPin, Briefcase, IndianRupee, 
  Clock, Calendar, GraduationCap, CheckCircle2, X, CheckCircle, 
  Users, MonitorPlay, MessageSquare, ShieldAlert, Sparkles, ChevronRight, FileText, Coins
} from 'lucide-react';
import Header from '../components/Header';

const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

export default function JobDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [similarJobs, setSimilarJobs] = useState([]);
  const [loadingSimilar, setLoadingSimilar] = useState(false);

  const [isCoinSystemActive, setIsCoinSystemActive] = useState(false);
  const [gatewayType, setGatewayType] = useState('razorpay'); 
  const [dynamicQRCode, setDynamicQRCode] = useState(null); 
  const [showQRModal, setShowQRModal] = useState(false);
  const [utrNumber, setUtrNumber] = useState('');
  const [submittingUtr, setSubmittingUtr] = useState(false);

  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState(false);

  const [hasApplied, setHasApplied] = useState(false);

  const [applicationData, setApplicationData] = useState({
    name: '',
    email: '',
    phone: '',
    resume_file: null,
  });

  const getUpiId = () => {
    return (import.meta.env && import.meta.env.VITE_UPI_ID) 
           || (process.env && process.env.REACT_APP_UPI_ID) 
           || "kadte74110630@barodampay";
  };

  useEffect(() => {
    const fetchJobAndSimilar = async () => {
      setLoading(true);
      try {
        try {
          const settingsRes = await api.get("/settings");
          if (settingsRes.data) {
            setIsCoinSystemActive(settingsRes.data.candidate_coin_system == 1);
            setGatewayType(settingsRes.data.payment_gateway || 'razorpay');
            setDynamicQRCode(settingsRes.data.qr_code_image || null); 
          }
        } catch (setErr) {
          console.warn("Failed to fetch settings for coin system", setErr);
        }

        const response = await api.get(`/job-details/${id}`);
        const currentJob = response.data.data || response.data;
        setJob(currentJob);

        const token = localStorage.getItem('candidateToken');
        if (token && currentJob) {
          try {
            const appRes = await api.get('/candidate/applications', {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            const apps = appRes.data.data || [];
            
            const myApp = apps.find(app => app.job?.title === currentJob.title);
            if (myApp) {
              const hoursPassed = (new Date() - new Date(myApp.applied_on)) / (1000 * 60 * 60);
              if (hoursPassed < 48) {
                setHasApplied(true);
              }
            }
          } catch (e) {
            console.error("Error checking application status:", e);
          }
        }

        if (currentJob.category) {
          setLoadingSimilar(true);
          try {
            const simRes = await api.get(`/public-jobs?category=${encodeURIComponent(currentJob.category)}`);
            let fetchedSimilar = simRes.data;
            fetchedSimilar = fetchedSimilar.filter(j => j.id.toString() !== id.toString());
            setSimilarJobs(fetchedSimilar.slice(0, 6)); 
          } catch (e) {
            console.error("Error fetching similar jobs", e);
          } finally {
            setLoadingSimilar(false);
          }
        }
      } catch (error) {
        console.error("Error fetching job details:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchJobAndSimilar();
    window.scrollTo(0, 0); 

    const savedCandidate = localStorage.getItem("candidate");
    if (savedCandidate) {
      try {
        const parsed = JSON.parse(savedCandidate);
        setApplicationData(prev => ({
          ...prev,
          name: parsed.name || '',
          email: parsed.email || '',
          phone: parsed.phone || '',
        }));
      } catch (e) {
        console.error("Error parsing candidate data", e);
      }
    }
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setApplicationData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setApplicationData(prev => ({ ...prev, resume_file: e.target.files[0] }));
  };

  const processApplication = async () => {
    setSubmitting(true);
    const formData = new FormData();
    formData.append('name', applicationData.name);
    formData.append('email', applicationData.email);
    formData.append('phone', applicationData.phone);
    formData.append('job_id', id);
    formData.append('status', 'Applied');
    if (applicationData.resume_file) {
      formData.append('resume_file', applicationData.resume_file);
    }

    try {
      const token = localStorage.getItem('candidateToken');

      await api.post("/candidate/apply", formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}` 
        } 
      });

      setSuccessMsg(true);
      setHasApplied(true); 
      
      // 🌟 NAYA: Screen par turant 1 vacancy kam karke dikhane ke liye
      setJob(prev => ({ ...prev, vacancies: prev.vacancies > 0 ? prev.vacancies - 1 : 0 }));

      setTimeout(() => {
        setIsApplyModalOpen(false);
        setSuccessMsg(false);
      }, 2500);
    } catch (error) {
      if (error.response && error.response.status === 403) {
        toast.error(error.response.data.message);
        setHasApplied(true); 
        setIsApplyModalOpen(false);
      }
      else if (error.response && error.response.status === 402) {
        if (gatewayType === 'qr') {
          toast.error("Insufficient Coins! Please recharge via QR.");
          setIsApplyModalOpen(false); 
          setShowQRModal(true);       
        } else {
          toast.error("Insufficient Coins! Initiating Recharge...");
          initiateRazorpayPayment();
        }
      } else {
        toast.error(error.response?.data?.message || "Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const initiateRazorpayPayment = async () => {
    setSubmitting(true);
    const scriptLoaded = await loadRazorpayScript();
    
    if (!scriptLoaded) {
      toast.error("Razorpay SDK failed to load. Check your internet connection.");
      setSubmitting(false);
      return;
    }

    try {
      const candidateData = JSON.parse(localStorage.getItem('candidate') || '{}');
      const token = localStorage.getItem('candidateToken'); 
      
      const orderRes = await api.post('/create-order', {
        amount: 10,                   
        coins: 100,                    
        candidate_id: candidateData.id, 
        user_type: 'candidate',       
        purpose: 'Wallet Recharge for Job Application'
      }, {
        headers: { 'Authorization': `Bearer ${token}` } 
      });

      const orderData = orderRes.data;

      if (!orderData || !orderData.order_id) {
        toast.error("Backend failed to generate Order ID.");
        setSubmitting(false);
        return;
      }

      let razorpayKey = orderData.key;
      if (!razorpayKey) {
        if (typeof process !== 'undefined' && process.env.REACT_APP_RAZORPAY_KEY) {
          razorpayKey = process.env.REACT_APP_RAZORPAY_KEY;
        } else if (import.meta && import.meta.env && import.meta.env.VITE_RAZORPAY_KEY) {
          razorpayKey = import.meta.env.VITE_RAZORPAY_KEY;
        }
      }

      if (!razorpayKey) {
        toast.error("Razorpay Key is missing!");
        setSubmitting(false);
        return;
      }

      const options = {
        key: razorpayKey,
        amount: orderData.amount, 
        currency: orderData.currency || "INR",
        name: "Kadtech HR Solutions",
        description: "Recharge Wallet to Apply",
        order_id: orderData.order_id, 
        handler: async function (response) {
          try {
            toast.loading("Verifying Payment...", { id: "verify" });
            const verifyRes = await api.post('/verify-payment', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            }, {
              headers: { 'Authorization': `Bearer ${token}` } 
            });

            if (verifyRes.data.success || verifyRes.status === 200) {
              toast.success("Recharge Successful! Submitting Application...", { id: "verify" });
              
              const savedCandidate = JSON.parse(localStorage.getItem('candidate') || '{}');
              savedCandidate.coins = (savedCandidate.coins || 0) + 100; 
              localStorage.setItem('candidate', JSON.stringify(savedCandidate));

              processApplication(); 
            }
          } catch (err) {
            toast.error("Payment Verification Failed!", { id: "verify" });
            console.error("Verification Error:", err);
          }
        },
        prefill: {
          name: applicationData.name,
          email: applicationData.email,
          contact: applicationData.phone
        },
        theme: {
          color: "#FED547"
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', function (response){
        toast.error("Payment Failed: " + response.error.description);
      });
      rzp.open();

    } catch (error) {
      console.error("🚨 Payment Error:", error);
      toast.error(error.response?.data?.message || error.message || "Failed to initiate payment.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleApplySubmit = (e) => {
    e.preventDefault();
    processApplication();
  };

  const parseArray = (data) => {
    if (!data) return [];
    if (typeof data === 'string') {
      try { return JSON.parse(data); } catch { return []; }
    }
    return data;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <Header />
        <div className="flex flex-col items-center justify-center py-32 text-gray-400">
          <div className="w-12 h-12 border-4 border-[#FED547] border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="font-medium">Loading premium job details...</p>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <Header />
        <div className="text-center py-32">
          <h2 className="text-2xl font-bold text-gray-800">Job Not Found</h2>
          <button onClick={() => navigate(-1)} className="mt-4 text-[#F58634] hover:underline">Go Back</button>
        </div>
      </div>
    );
  }

  const skillsArray = parseArray(job.skills);
  const benefitsArray = parseArray(job.benefits);
  const employerName = job.employer?.company_name || job.company_name || job.employer?.name || "Kadtech HR Solutions";
  const locationText = job.city ? (job.locality ? `${job.locality}, ${job.city}` : job.city) : (job.location || 'Not Specified');

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-20 font-sans">
      <Header />

      <div className="max-w-[1200px] mx-auto px-4 pt-6 pb-2">
        <button onClick={() => navigate('/job-listing')} className="flex items-center text-sm font-bold text-gray-500 hover:text-blue-600 transition-colors">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Search Results
        </button>
      </div>

      <div className="max-w-[1200px] mx-auto px-4 mt-4 flex flex-col lg:flex-row gap-6">
        
        <div className="w-full lg:w-[68%] space-y-6">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-[#FED547] to-[#F58634]"></div>
            <div className="flex flex-col md:flex-row justify-between items-start gap-4 mt-2">
              <div>
                <h1 className="text-2xl md:text-3xl font-black text-gray-900 leading-tight mb-2">{job.title}</h1>
                <p className="text-lg font-bold text-gray-700 flex items-center mb-5">
                  <Building2 className="w-5 h-5 mr-2 text-gray-400" />
                  {employerName}
                </p>
                <div className="flex flex-wrap gap-x-6 gap-y-3 text-sm font-semibold text-gray-700">
                  <span className="flex items-center"><MapPin className="w-4 h-4 mr-2 text-gray-400"/> {locationText}</span>
                  <span className="flex items-center"><Briefcase className="w-4 h-4 mr-2 text-gray-400"/> {job.freshers_only ? "Fresher" : (job.experience || 'Not Specified')}</span>
                  <span className="flex items-center"><IndianRupee className="w-4 h-4 mr-1.5 text-gray-400"/> {job.salary_range || 'Not Disclosed'}</span>
                </div>
              </div>

              <div className="flex flex-col gap-2 shrink-0 md:text-right w-full md:w-auto mt-4 md:mt-0">
                <span className="bg-green-100 text-green-800 text-xs px-3 py-1.5 rounded-md font-bold uppercase tracking-wider text-center md:inline-block">
                  Active Hiring
                </span>
                {job.contractual && (
                  <span className="bg-orange-100 text-orange-800 text-xs px-3 py-1.5 rounded-md font-bold uppercase tracking-wider text-center md:inline-block">
                    Contractual Role
                  </span>
                )}
              </div>
            </div>
            
            <div className="mt-8 pt-5 border-t border-gray-100 flex flex-wrap gap-2.5">
              <span className="bg-gray-50 border border-gray-200 text-gray-700 text-xs font-bold px-3 py-1.5 rounded-md flex items-center gap-1.5"><Clock className="w-3.5 h-3.5"/> {job.job_type || 'Full-Time'}</span>
              <span className="bg-gray-50 border border-gray-200 text-gray-700 text-xs font-bold px-3 py-1.5 rounded-md flex items-center gap-1.5"><MonitorPlay className="w-3.5 h-3.5"/> {job.work_location_type || 'Work from office'}</span>
              <span className="bg-gray-50 border border-gray-200 text-gray-700 text-xs font-bold px-3 py-1.5 rounded-md flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5"/> {job.shift || 'Day'} Shift</span>
              <span className="bg-gray-50 border border-gray-200 text-gray-700 text-xs font-bold px-3 py-1.5 rounded-md flex items-center gap-1.5"><Users className="w-3.5 h-3.5"/> {job.working_days || 'Not Specified'}</span>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8">
            <h3 className="text-lg font-extrabold text-gray-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#FED547]" /> Job Description
            </h3>
            <div className="prose max-w-none text-gray-700 text-[15px] whitespace-pre-line leading-relaxed font-medium">
              {job.description || "No detailed description provided."}
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8">
            <h3 className="text-lg font-extrabold text-gray-900 mb-6 flex items-center gap-2">
              <Users className="w-5 h-5 text-[#FED547]" /> Who can apply?
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="flex items-start gap-3">
                <div className="p-2.5 bg-blue-50 text-blue-600 rounded-lg shrink-0"><GraduationCap className="w-5 h-5"/></div>
                <div>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-0.5">Education</p>
                  <p className="text-sm font-bold text-gray-900">{job.qualification || job.course_education || 'Any'}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2.5 bg-green-50 text-green-600 rounded-lg shrink-0"><MessageSquare className="w-5 h-5"/></div>
                <div>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-0.5">English Level</p>
                  <p className="text-sm font-bold text-gray-900">{job.english_fluency || 'Not Specified'}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2.5 bg-purple-50 text-purple-600 rounded-lg shrink-0"><Briefcase className="w-5 h-5"/></div>
                <div>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-0.5">Experience</p>
                  <p className="text-sm font-bold text-gray-900">{job.freshers_only ? "Fresher Only" : (job.experience || 'Any')}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2.5 bg-orange-50 text-orange-600 rounded-lg shrink-0"><Users className="w-5 h-5"/></div>
                <div>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-0.5">Gender Preference</p>
                  <p className="text-sm font-bold text-gray-900">{job.gender || 'Any'}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {benefitsArray.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-[16px] font-extrabold text-gray-900 mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#FED547]" /> Perks & Benefits
                </h3>
                <ul className="space-y-3">
                  {benefitsArray.map((ben, i) => (
                    <li key={i} className="flex items-center text-sm font-bold text-gray-700">
                      <CheckCircle2 className="w-4 h-4 mr-2 text-teal-500 shrink-0" /> {ben}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {job.interview_type && (
              <div className="bg-gray-50 rounded-2xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-[16px] font-extrabold text-gray-900 mb-4 flex items-center gap-2">
                  <MonitorPlay className="w-5 h-5 text-[#FED547]" /> Interview Info
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between border-b border-gray-200 pb-2">
                    <span className="font-semibold text-gray-500">Format:</span>
                    <span className="font-bold text-gray-900">{job.interview_type}</span>
                  </div>
                  <div className="flex justify-between border-b border-gray-200 pb-2">
                    <span className="font-semibold text-gray-500">Deposit / Fees:</span>
                    <span className="font-bold text-gray-900 flex items-center gap-1">
                      {job.deposit_required === "Yes" ? <ShieldAlert className="w-4 h-4 text-red-500"/> : <CheckCircle2 className="w-4 h-4 text-green-500"/>}
                      {job.deposit_required || "No"}
                    </span>
                  </div>
                  {job.interview_type === "In-person" && (
                    <div className="pt-1">
                      <span className="font-semibold text-gray-500 block mb-1">Interview Location:</span>
                      <span className="font-bold text-gray-900">{job.interview_locality}, {job.interview_city}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {skillsArray.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8">
              <h3 className="text-lg font-extrabold text-gray-900 mb-4">Required Skills</h3>
              <div className="flex flex-wrap gap-2.5">
                {skillsArray.map((skill, index) => (
                  <span key={index} className="bg-gray-100 text-gray-800 font-bold px-4 py-2 rounded-full text-[13px] border border-gray-200 shadow-sm">
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="w-full lg:w-[32%]">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 sticky top-24">
            
            <h3 className="text-lg font-extrabold text-gray-900 mb-5">Job Summary</h3>
            
            <div className="space-y-4 mb-8">
              <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                <span className="text-sm font-semibold text-gray-500">Job Role</span>
                <span className="text-sm font-bold text-gray-900 text-right">{job.category || 'General'}</span>
              </div>
              <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                <span className="text-sm font-semibold text-gray-500">Openings</span>
                <span className="text-sm font-bold text-gray-900 text-right">{job.vacancies || 1}</span>
              </div>
              <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                <span className="text-sm font-semibold text-gray-500">Industry</span>
                <span className="text-sm font-bold text-gray-900 text-right">{job.industry || 'Any'}</span>
              </div>
              <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                <span className="text-sm font-semibold text-gray-500">Apply Before</span>
                <span className="text-sm font-bold text-red-600 text-right">
                  {job.deadline ? new Date(job.deadline).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'ASAP'}
                </span>
              </div>
            </div>

            <button 
              onClick={() => {
                if (!hasApplied) setIsApplyModalOpen(true);
              }}
              disabled={hasApplied}
              className={`w-full flex justify-center items-center gap-2 font-extrabold py-3.5 rounded-xl shadow-md transition-all duration-300 transform ${
                hasApplied 
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                  : 'bg-[#FED547] hover:bg-[#000000] hover:text-[#FED547] text-black hover:-translate-y-1'
              }`}
            >
              {hasApplied ? 'Applied' : 'Apply For This Job'} 
              {!hasApplied && <ChevronRight className="w-4 h-4" />}
            </button>
            
            {/* 🌟 NAYA: 48 Hours wala message aur Terms */}
            {hasApplied ? (
              <p className="text-xs font-bold text-red-500 text-center mt-4">
                You have already applied. You can re-apply after 48 hours.
              </p>
            ) : (
              <p className="text-xs font-semibold text-gray-400 text-center mt-4">
                By applying, you agree to our Terms & Conditions.
              </p>
            )}
          </div>
        </div>

      </div>

      {similarJobs.length > 0 && (
        <div className="max-w-[1200px] mx-auto px-4 mt-16 pt-8 border-t border-gray-200">
          <div className="flex justify-between items-end mb-6">
            <div>
              <h2 className="text-2xl font-extrabold text-gray-900">Similar Jobs</h2>
              <p className="text-sm font-medium text-gray-500 mt-1">Explore other opportunities in {job.category}</p>
            </div>
            <button onClick={() => navigate(`/job-listing?category=${encodeURIComponent(job.category)}`)} className="text-sm font-bold text-blue-600 hover:underline hidden sm:block">
              View All
            </button>
          </div>

          <div className="flex overflow-x-auto gap-6 pb-6 snap-x hide-scrollbar" style={{ scrollbarWidth: 'none' }}>
            {similarJobs.map((sJob) => {
              const sEmployer = sJob.employer?.company_name || sJob.employer?.name || "Kadtech HR Employer";
              const sLocation = sJob.city ? (sJob.locality ? `${sJob.locality}, ${sJob.city}` : sJob.city) : (sJob.location || 'Not Specified');
              
              return (
                <div 
                  key={sJob.id} 
                  onClick={() => navigate(`/job-details/${sJob.id}`)}
                  className="min-w-[280px] md:min-w-[320px] bg-white rounded-xl shadow-sm border border-gray-200 p-5 cursor-pointer hover:border-[#FED547] hover:shadow-md transition-all snap-start flex flex-col group"
                >
                  <h3 className="font-extrabold text-gray-900 text-base leading-tight mb-1 group-hover:text-blue-600 line-clamp-1">{sJob.title}</h3>
                  <p className="text-xs font-bold text-gray-500 mb-4">{sEmployer}</p>
                  
                  <div className="space-y-2 mt-auto">
                    <div className="flex items-center text-xs font-semibold text-gray-600">
                      <IndianRupee className="w-3.5 h-3.5 mr-2 text-gray-400"/> {sJob.salary_range || 'Not Disclosed'}
                    </div>
                    <div className="flex items-center text-xs font-semibold text-gray-600">
                      <MapPin className="w-3.5 h-3.5 mr-2 text-gray-400"/> <span className="truncate">{sLocation}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {isApplyModalOpen && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm p-4">
          <div className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl p-6 md:p-8 animate-in fade-in zoom-in-95 duration-200">
            <button onClick={() => setIsApplyModalOpen(false)} className="absolute top-5 right-5 p-1.5 bg-gray-100 rounded-full text-gray-500 hover:bg-red-50 hover:text-red-500 transition-colors">
              <X className="w-5 h-5" />
            </button>

            <h3 className="text-2xl font-extrabold text-gray-900 mb-1">Apply Now</h3>
            <p className="text-sm font-semibold text-gray-500 mb-6 pb-4 border-b border-gray-100">You are applying for <span className="text-black">{job.title}</span></p>

            {successMsg ? (
              <div className="py-8 text-center space-y-4">
                <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-2">
                  <CheckCircle className="w-10 h-10 text-green-500 animate-bounce" />
                </div>
                <h4 className="text-xl font-extrabold text-gray-900">Application Sent!</h4>
                <p className="text-sm font-medium text-gray-500 max-w-[80%] mx-auto">Your profile has been shared with the HR. Keep an eye on your email/phone.</p>
              </div>
            ) : (
              <form onSubmit={handleApplySubmit} className="space-y-5">
                <div>
                  <label className="block text-[11px] font-extrabold text-gray-500 mb-1.5 uppercase tracking-wider">Full Name *</label>
                  <input type="text" name="name" value={applicationData.name} onChange={handleInputChange} required className="w-full border-2 border-gray-200 rounded-xl px-4 py-2.5 text-sm font-bold text-gray-900 focus:outline-none focus:border-[#FED547] bg-gray-50 focus:bg-white transition-colors" placeholder="Enter your full name" />
                </div>

                <div>
                  <label className="block text-[11px] font-extrabold text-gray-500 mb-1.5 uppercase tracking-wider">Email Address *</label>
                  <input type="email" name="email" value={applicationData.email} onChange={handleInputChange} required className="w-full border-2 border-gray-200 rounded-xl px-4 py-2.5 text-sm font-bold text-gray-900 focus:outline-none focus:border-[#FED547] bg-gray-50 focus:bg-white transition-colors" placeholder="Enter your email" />
                </div>

                <div>
                  <label className="block text-[11px] font-extrabold text-gray-500 mb-1.5 uppercase tracking-wider">Phone Number *</label>
                  <input type="tel" name="phone" value={applicationData.phone} onChange={handleInputChange} required className="w-full border-2 border-gray-200 rounded-xl px-4 py-2.5 text-sm font-bold text-gray-900 focus:outline-none focus:border-[#FED547] bg-gray-50 focus:bg-white transition-colors" placeholder="Enter mobile number" />
                </div>

                <div>
                  <label className="block text-[11px] font-extrabold text-gray-500 mb-1.5 uppercase tracking-wider">Upload Resume (PDF / Doc) *</label>
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-3 text-center hover:border-blue-400 transition-colors cursor-pointer bg-blue-50/30">
                    <input type="file" onChange={handleFileChange} accept=".pdf,.doc,.docx" required className="w-full text-sm font-bold text-gray-500 file:mr-4 file:py-2.5 file:px-5 file:rounded-lg file:border-0 file:text-xs file:font-extrabold file:bg-blue-100 file:text-blue-700 hover:file:bg-blue-200 file:cursor-pointer" />
                  </div>
                </div>

                <button type="submit" disabled={submitting} className="w-full bg-[#FED547] hover:bg-[#000000] hover:text-[#FED547] text-black font-extrabold py-3.5 rounded-xl transition-all shadow-md mt-4 disabled:opacity-70 flex justify-center items-center">
                  {submitting ? (
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    isCoinSystemActive ? 'Submit Application (Costs 100 🪙)' : 'Submit Application'
                  )}
                </button>
              </form>
            )}
          </div>
        </div>,
        document.body
      )}

      {showQRModal && createPortal(
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="relative w-full max-w-sm bg-white rounded-3xl shadow-2xl p-6 md:p-8 animate-in fade-in zoom-in-95">
            <button onClick={() => setShowQRModal(false)} className="absolute top-4 right-4 p-1.5 bg-gray-100 rounded-full text-gray-500 hover:bg-red-50 hover:text-red-500 transition-colors">
              <X className="w-5 h-5" />
            </button>

            <div className="text-center mb-6">
              <h3 className="text-xl font-extrabold text-gray-900 mb-1">Recharge Wallet</h3>
              <p className="text-sm font-semibold text-gray-500">Scan & Pay ₹100 for 1000 Coins</p>
            </div>

            <div className="bg-gray-50 p-4 rounded-2xl border-2 border-dashed border-gray-200 flex justify-center mb-6">
              <img 
                src={`https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(
                  `upi://pay?pa=${getUpiId()}&pn=Kadtech HR Solutions&am=100.00&cu=INR`
                )}`} 
                alt="Company UPI QR Code" 
                className="w-48 h-48 object-contain rounded-xl shadow-sm" 
              />
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-[11px] font-extrabold text-red-500 mb-1.5 uppercase tracking-wider">Enter 12-Digit UTR No. to get the Coins!</label>
                <input 
                  type="text" 
                  value={utrNumber} 
                  onChange={(e) => setUtrNumber(e.target.value.replace(/\D/g, '').slice(0, 12))} 
                  placeholder="e.g. 123456789012" 
                  className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-sm font-bold text-gray-900 focus:outline-none focus:border-yellow-500 bg-gray-50 focus:bg-white transition-colors text-center tracking-widest" 
                />
              </div>

              <button 
                onClick={async () => {
                  if (utrNumber.length < 12) return toast.error("Please enter a valid 12-digit UTR!");
                  setSubmittingUtr(true);
                  try {
                    const candidateData = JSON.parse(localStorage.getItem('candidate') || '{}');
                    const token = localStorage.getItem('candidateToken');
                    await api.post('/submit-utr', { candidate_id: candidateData.id, utr_number: utrNumber }, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    toast.success("UTR Submitted! Admin will verify and add coins soon.");
                    setShowQRModal(false);
                    setUtrNumber('');
                  } catch (err) {
                    toast.error(err.response?.data?.message || "Failed to submit UTR");
                  } finally {
                    setSubmittingUtr(false);
                  }
                }}
                disabled={submittingUtr || utrNumber.length < 12} 
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-extrabold py-3.5 rounded-xl transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submittingUtr ? "Submitting..." : "Submit Payment Request"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

    </div>
  );
}